import React, { useRef, useEffect, useState } from 'react';
import { message } from '../Message';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import withBackend from '../../backend/withBackend';
import { setUser } from '../../actions';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Snackbar } from '@mui/material';
import useTurnstile from '../../custom-hooks/useTurnstile';
import { checkRateLimit, formatWaitTime, getRateLimitReset } from '../../utils/rateLimiter';
import { validateAndSanitizeEmail, sanitizeLanguageCode, escapeHtml } from '../../utils/security';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const GiveawayModal = ({ isOpen, onClose, onSubmitted, isSubmitted, currentLanguage, engine, setUser }) => {
  const popupRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [openCoupon, setOpenCoupon] = useState(false)
  const [initializeTurnstile, setInitializeTurnstile] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [isDisplayDelayElapsed, setIsDisplayDelayElapsed] = useState(false);
  const [isGoogleLoginFlow, setIsGoogleLoginFlow] = useState(false);
  const [isEmailLinkSubmitted, setIsEmailLinkSubmitted] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const shouldDisplayModal = isOpen && isDisplayDelayElapsed;
  const shouldShowSubmittedState = isSubmitted || isEmailLinkSubmitted;
  
  // Initialize Turnstile only when user interacts with email input
  const { containerRef, token, isLoading: isTurnstileLoading, error: turnstileError, reset } = useTurnstile({
    siteKey: initializeTurnstile ? TURNSTILE_SITE_KEY : null,
    theme: 'auto',
    size: 'normal',
  });

  useEffect(() => {
    const handleClickOutside = event => {
      if (!shouldDisplayModal) return;
      if (shouldShowSubmittedState) return;

      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose, shouldDisplayModal, shouldShowSubmittedState]);

  useEffect(() => {
    if (isDisplayDelayElapsed) return;

    const timerId = setTimeout(() => {
      setIsDisplayDelayElapsed(true);
    }, 15000);

    return () => {
      clearTimeout(timerId);
    };
  }, [isDisplayDelayElapsed]);

  // Initialize Turnstile when user clicks on email input (only if not already verified)
  const handleEmailInputFocus = () => {
    if (TURNSTILE_SITE_KEY && !token) {
      if (!initializeTurnstile) {
        setInitializeTurnstile(true);
        setShowTurnstile(true);
      } else if (!showTurnstile) {
        setShowTurnstile(true);
      }
    }
  };

  // Hide Turnstile when verification is successful
  useEffect(() => {
    if (token && showTurnstile) {
      setShowTurnstile(false);
    }
  }, [token, showTurnstile]);

  // Show Turnstile again only if there's an error and no valid token
  useEffect(() => {
    if (turnstileError && !token && initializeTurnstile && TURNSTILE_SITE_KEY) {
      setShowTurnstile(true);
    }
  }, [turnstileError, token, initializeTurnstile]);

  // Handle email change
  const handleEmailChange = (e) => {
    const rawEmail = e.target.value;
    
    // Validate and sanitize email input
    const { isValid, sanitized, error } = validateAndSanitizeEmail(rawEmail);
    
    // Set the sanitized value
    setEmail(sanitized);
    setEmailError('');

    // If validation failed during typing, show error only if user typed enough
    if (!isValid && sanitized.length > 3 && error) {
      setEmailError(error);
      return;
    }

  };

  const subscribeToGiveaway = async (safeEmail) => {
    const apiUrl = 'https://reok.lemonsqueezy.com/email-subscribe/external';
    // Use a native form post in a hidden iframe to bypass CORS limits on XHR/fetch.
    // Lemon Squeezy signup endpoint is documented for HTML form submissions.
    // See: https://docs.lemonsqueezy.com/help/email/signup-forms
    const targetName = `lemon-signup-${Date.now()}`;
    const iframe = document.createElement('iframe');
    iframe.name = targetName;
    iframe.style.display = 'none';

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = apiUrl;
    form.target = targetName;
    form.style.display = 'none';

    const emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.value = safeEmail;
    form.appendChild(emailInput);

    document.body.appendChild(iframe);
    document.body.appendChild(form);

    try {
      form.submit();
      return true;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        form.remove();
        iframe.remove();
      }, 1000);
    }
  };

  // Handle Google sign-in
  const handleSubmitWithGoogle = async (emailForSubscribe) => {
    try {
      const response = await engine.signInWithGoogle();
      const oauthEmail = response?.user?.email || emailForSubscribe;

      const userSnapshot = await engine.getUser(response.user.uid);
      
      if (userSnapshot.data()) {
        // Existing user - redirect to app
        const userData = userSnapshot.data();
        setUser(userData);
        message.success(t('message.success.loginSuccess'));
        onSubmitted(true);
        onClose();
        
        // Navigate to app with language prefix if needed (sanitized to prevent path traversal)
        const safeLang = sanitizeLanguageCode(currentLanguage);
        const appUrl = safeLang === 'en' 
          ? '/app' 
          : `/${safeLang}/app`;
        navigate(appUrl);
      } else {
        // New user - create account and redirect to app
        const initialUserData = {
          uid: response.user.uid,
          role: "USER",
          email: response.user.email,
          joinedDate: new Date().getTime(),
          firstName: response.user.displayName || '',
          emailVerified: true,
          status: "incomplete",
          isActivated: true,
          hasSubscription: false,
          proTrial: process.env.REACT_APP_PRO_TRIAL,
          basicTrial: process.env.REACT_APP_BASIC_TRIAL,
          upscaleTrial: process.env.REACT_APP_UPSCALE_TRIAL,
          pro: false,
          basic: true,
          trial: true,
          proCredits: 0,
          basicCredits: 30,
          refCode: ''
        };

        await engine.addUser(response.user.uid, initialUserData);
        const newUserSnapshot = await engine.getUser(response.user.uid);
        setUser(newUserSnapshot.data());
        message.success(t('message.success.signupSuccess'));
        onSubmitted(true);
        onClose();
        
        // Navigate to app with language prefix if needed (sanitized to prevent path traversal)
        const safeLang = sanitizeLanguageCode(currentLanguage);
        const appUrl = safeLang === 'en' 
          ? '/app' 
          : `/${safeLang}/app`;
        navigate(appUrl);
      }

      if (oauthEmail) {
        try {
          const subscribed = await subscribeToGiveaway(oauthEmail);
          if (!subscribed) {
            message.warning(t('giveAway.subscribeRetryAfterLogin'));
          }
        } catch (subscribeError) {
          message.warning(t('giveAway.subscribeRetryAfterLogin'));
        }
      }

      if (TURNSTILE_SITE_KEY) {
        reset();
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Handle specific error cases with user-friendly messages
      if (error.code === 'auth/popup-closed-by-user') {
        message.warning(t('message.error.googlePopupClosed'));
      } else if (error.code === 'auth/cancelled-popup-request') {
        // Silent - user opened multiple popups
        message.warning(t('message.error.googlePopupClosed'));
      } else if (error.code === 'auth/popup-blocked') {
        message.error(t('message.error.googlePopupBlocked'));
      } else {
        message.error(error.message || t('message.error.googleSignInFailed'));
      }
    }
  };

  const handleSubmit = async () => {
    if (loading) return;

    // Validate email
    if (!email) {
      setEmailError(t('message.error.emptyEmail'));
      return;
    }

    // Comprehensive security validation
    const validation = validateAndSanitizeEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.error || t('message.error.validEmail'));
      return;
    }

    // Use sanitized email for all operations
    const safeEmail = validation.sanitized;

    // Check Turnstile token (only if site key is configured)
    if (TURNSTILE_SITE_KEY && !token) {
      message.error(t('message.error.completeCaptcha'));
      return;
    }

    // Client-side rate limiting: max 5 attempts per email within 10 minutes
    if (!checkRateLimit('giveaway-signup', safeEmail, { maxAttempts: 5, windowMs: 10 * 60 * 1000 })) {
      const resetTime = getRateLimitReset('giveaway-signup', safeEmail, 10 * 60 * 1000);
      message.error(
        t('message.error.tooManyAttempts', { 
          time: formatWaitTime(resetTime) 
        })
      );
      return;
    }

    setLoading(true);
    setIsGoogleLoginFlow(false);

    try {
      if (safeEmail.toLowerCase().endsWith('@gmail.com')) {
        setIsGoogleLoginFlow(true);
        await handleSubmitWithGoogle(safeEmail);
        return;
      }

      const subscribed = await subscribeToGiveaway(safeEmail);
      if (!subscribed) {
        message.error(t('message.error.subscribeError'));
        if (TURNSTILE_SITE_KEY) {
          reset();
        }
        return;
      }

      setIsEmailLinkSubmitted(true);
      handleSubmitWithEmailLink(safeEmail);
      // Reset Turnstile for next use (only if configured)
      if (TURNSTILE_SITE_KEY) {
        reset();
      }
      // onClose();
    } catch (error) {
      message.error(t('message.error.subscribeError'));
      if (TURNSTILE_SITE_KEY) {
        reset();
      }
    } finally {
      setLoading(false);
      setIsGoogleLoginFlow(false);
    }
  };

  const handleSubmitWithEmailLink = (email) => {
    try {

      const baseUrl = window.location.origin;
      const redirectUrl = currentLanguage === 'en'
        ? `${baseUrl}/verify-email/?email=${email}`
        : `${baseUrl}/${currentLanguage}/verify-email/?email=${email}`;

      const actionCodeSettings = {
        url: redirectUrl,
        handleCodeInApp: true,
      };

      engine
        .sendSignInLinkToEmail(email, actionCodeSettings)
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.log(error);
        });

    } catch (error) {
      console.log(error);
    }
  };

  const handleGoogleFromSuccessMessage = async () => {
    if (loading) return;

    setLoading(true);
    setIsGoogleLoginFlow(true);

    try {
      await handleSubmitWithGoogle(email);
    } finally {
      setLoading(false);
      setIsGoogleLoginFlow(false);
    }
  };

  // Don't return null - keep mounted for Turnstile pre-loading
  // Modal is hidden via CSS when isOpen is false

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out"
        style={{ 
          opacity: shouldDisplayModal ? 1 : 0, 
          pointerEvents: shouldDisplayModal ? 'auto' : 'none',
          visibility: shouldDisplayModal ? 'visible' : 'hidden' // Hide when closed but keep mounted
        }}>
        <div ref={popupRef}
          className="relative bg-black rounded-lg shadow-lg p-5 max-w-[412px] mx-auto text-white transition-all duration-300 ease-in-out"
          style={{ transform: shouldDisplayModal ? 'scale(1)' : 'scale(0.9)', opacity: shouldDisplayModal ? 1 : 0, border: "1px solid #343434" }}>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-white transition-all duration-200 hover:rotate-90 hover:scale-110 focus:outline-none group"
            aria-label="Close modal"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
          <div className="flex justify-center mb-4">
            <svg width="50" height="50" viewBox="0 0 0.875 0.875" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="bounce-rotate-animation">
              <path fill="#d32f2f" d="M0.729 0.813H0.146a0.042 0.042 0 0 1 -0.042 -0.042V0.354h0.667v0.417c0 0.023 -0.019 0.042 -0.042 0.042" />
              <path fill="#f44336" d="M0.813 0.354H0.063v-0.125C0.063 0.206 0.081 0.188 0.104 0.188h0.667c0.023 0 0.042 0.019 0.042 0.042z" />
              <path fill="#ff8f00" d="M0.396 0.354h0.083V0.813H0.396zm0.125 -0.292 -0.125 0.125h0.083l0.125 -0.125z" />
              <path fill="#ffc107" d="M0.396 0.354h0.083V0.188l-0.125 -0.125H0.271l0.125 0.125z" />
            </svg>
          </div>

          <h2 className="text-center text-2xl font-bold mb-2">
            <div className="mx-auto relative z-[10] text-center mx-auto md:text-center label-giveaway">
              <span>{t('giveAway.title')}</span>
            </div>
          </h2>

          <p className="text-center text-gray-400 mb-6">
            {t('giveAway.subTitle')}
          </p>
          {shouldShowSubmittedState ? (
            <div className="flex flex-col items-center justify-center mb-2">
              <hr className="w-full border-t border-gray-700 mb-6" />
              <div className="flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-white font-semibold">&nbsp;{t('giveAway.emailSent')}</span>
              </div>
              <h2 className="text-center text-2xl font-bold">
                <div className="mx-auto relative z-[10] text-center mx-auto md:text-center label-giveaway">
                  <span>{t('giveAway.emailSentTitle')}</span>&nbsp;🎉
                </div>
              </h2>
              <p className="text-gray-400 text-center">
                {t('giveAway.emailSentSubPrefix')}{' '}
                <button
                  type="button"
                  onClick={handleGoogleFromSuccessMessage}
                  disabled={loading}
                  className="text-blue-400 underline underline-offset-2 hover:text-blue-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {t('giveAway.googleWord')}
                </button>{' '}
                {t('giveAway.emailSentSubSuffix')}
              </p>
            </div>
          ) : (
            <>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleEmailChange}
                onFocus={handleEmailInputFocus}
                onClick={handleEmailInputFocus}
                placeholder={t('giveAway.enterYourEmail')}
                maxLength={254}
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full px-4 py-3 rounded-md border border-gray-700 mb-3 focus:outline-none text-white placeholder-gray-500" style={{ backgroundColor: '#151515' }}
              />
              {emailError && <p className="text-red text-sm mb-4">{emailError}</p>}

              {/* Cloudflare Turnstile CAPTCHA - show on email click, hide when verified */}
              {TURNSTILE_SITE_KEY && (
                <>
                  <div style={{ 
                    display: showTurnstile ? 'flex' : 'none', 
                    justifyContent: 'center', 
                    marginBottom: '16px' 
                  }}>
                    <div ref={containerRef} />
                  </div>
                  
                  {turnstileError && showTurnstile && !isTurnstileLoading && (
                    <p className="text-red text-sm mb-4 text-center">
                      {turnstileError}
                    </p>
                  )}
                </>
              )}

              <button
                onClick={handleSubmit}
                disabled={
                  loading || 
                  (TURNSTILE_SITE_KEY && (isTurnstileLoading || !token))
                }
                className="w-full bg-pink-500 text-white py-4 text-sm rounded-md hover:bg-yellow-400 hover:scale-105 transition duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? (isGoogleLoginFlow ? t('giveAway.signingInWithGoogle') : t('giveAway.submitting'))
                  : t('giveAway.notifyMe')}
              </button>
              {loading && isGoogleLoginFlow && (
                <p className="text-center text-xs text-gray-400 mt-3">
                  {t('giveAway.googleSignInWait')}
                </p>
              )}
              {/* <div onClick={handleSubmit} className="upgrade-button"
                style={{ cursor: 'pointer' }}
              >
                {loading ? t('giveAway.submitting') : t('giveAway.notifyMe')}
              </div> */}
              <div className="text-center text-sm text-gray-500 mt-4 mb-2">
                {t('tos.agree')}
                <a
                  className="agree-service" style={{ color: "#c2c2c2" }}
                  href={'/legal/?view=terms'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.terms')}
                </a>
                {' '}&{' '}
                <a
                  className="agree-service" style={{ color: "#c2c2c2" }}
                  href={'/legal/?view=privacy-policy'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.privacy')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
      <Snackbar
        open={openCoupon}
        onClose={() => setOpenCoupon(false)}
        autoHideDuration={1500}
        message="Copied REOK10"
      />
    </>
  );
};

export default compose(
  connect(null, { setUser }),
  withBackend
)(GiveawayModal);
